// Sections moved from apps/backoffice (Workflow Studio, Integrations, AI Governance, Audit & Security).
export default {
  governance: {
    agentCatalog: {
      tab: "Agents",
      hint: "Every AI component of the platform: what it is for, what it reads and produces, the deterministic tools it may call, the workflow steps that invoke it, who approves its proposals and which guardrails apply. Export the catalogue as Markdown for the client documentation set.",
      filter: "Filter agents…",
      count: "{{count}} agent(s)",
      export: "Export Markdown",
      categories: {
        WorkflowAgent: "Workflow agents",
        Assistant: "Assistants",
        Service: "Pipeline services"
      },
      deterministic: "Deterministic / pipeline",
      accepted: "accepted",
      inputs: "Reads",
      outputs: "Produces",
      runsAt: "Runs at",
      tools: "Tools",
      humanDecision: "Human decision",
      guardrails: "Guardrails",
      last30: "Last 30 days: {{runs}} run(s), {{proposals}} proposal(s)"
    },
    modelManaged: "Managed by the platform; the underlying provider is configured in the secure gateway.",
    title: "AI Governance",
    subtitle: "Model routing, guardrails, evaluations, drift, the risk register and the Arabic–English parity check for every AI function.",
    routing: "Model routing",
    guardrails: "Guardrails",
    evals: "Evaluations",
    drift: "Drift",
    pii: "PII redaction",
    prompts: "Prompt versions",
    feedback: "Human feedback",
    overview: "Overview",
    runs: "Agent runs",
    runsFooter: "{{failed}} failed · {{cases}} cases with AI assistance",
    quality: "AI quality",
    qualityFooter: "AI quality review {{score}}/100 over {{count}} proposals",
    revisionRuns: "{{count}} reviewer-requested revisions",
    tokens: "{{count}} tokens",
    tokensShort: "Tokens",
    tokensFooter: "{{input}} in · {{output}} out",
    acceptance: "Acceptance rate",
    acceptanceFooter: "{{edited}}% edited before approval · {{pending}} pending",
    confidence: "Avg confidence",
    confidenceFooter: "avg run {{ms}} ms",
    blockedFooter: "{{count}} blocked outputs",
    piiFooter: "{{tools}} tool calls · {{retrievals}} retrievals",
    offlineTitle: "Deterministic offline mode",
    offlineHint: "The AI gateway is not connected, so agents run their rule-based drafts. Confidence is capped at 45% until the gateway is enabled.",
    activity14d: "Activity – last 14 days",
    activityHint: "Runs, proposals and guardrail findings per day.",
    guardrailsByRule: "Guardrail findings by rule",
    guardrailsByRuleHint: "Every finding is recorded with the run that produced it.",
    events: "events",
    byAgent: "Runs and findings by agent",
    byAgentHint: "Top agents by volume.",
    usagePerCase: "Model usage per case",
    usagePerCaseHint: "Cases with the heaviest model usage; click to open the Agents tab.",
    agentTable: "Agent scorecard",
    agent: "Agent",
    model: "Model",
    proposals: "Proposals",
    latency: "Latency",
    tiers: {
      Reasoning: "Reasoning",
      Fast: "Fast",
      Vision: "Vision",
      Embedding: "Embedding",
      Stt: "Speech-to-text"
    },
    configured: "configured",
    overrides: "overrides",
    configuredDefault: "configured default",
    routeTable: "Model routes",
    routeTableHint: "One active route per tier; the active route overrides the configured default at runtime.",
    tier: "Tier",
    fallback: "Fallback",
    context: "Context",
    out: "out",
    usage: "Usage",
    active: "Active",
    inactive: "Inactive",
    effective: "In use",
    adminOnly: "Only the platform administrator can change routes.",
    editRoute: "Edit {{tier}} route",
    editRouteHint: "Changes apply immediately to new agent runs and are written to the audit trail.",
    notes: "Notes",
    activeForTier: "Active route for this tier",
    routeSaved: "Model route updated",
    // Condition 3.7: a model route changes through a release, approved by a second administrator.
    routeRelease: {
      hint: "A change to a model is an update under conditions 1.5 and 3.7: it goes into your draft release and takes effect only once a second administrator approves it under Releases.",
      staged: "Added to your draft release",
      stagedBody: "\"{{title}}\": submit it under Releases; the route changes once a second administrator approves it.",
      pending: "Change awaiting release",
    },
    guardrailEvents: "Guardrail events",
    guardrailEventsHint: "Findings from the output inspector: decision language, PII leaks, missing or unknown citations, numbers not produced by tools, prompt injection.",
    allRules: "All rules",
    allSeverities: "All severities",
    rule: "Rule",
    detail: "Detail",
    action: "Action",
    rules: {
      PromptInjection: "Prompt injection",
      DecisionLanguage: "Decision language",
      PiiLeak: "PII leak",
      MissingCitations: "Missing citations",
      UnknownCitation: "Unknown citation",
      NumberNotFromTool: "Number not from tool",
      LanguageMismatch: "Language mismatch",
      ArabicParity: "Arabic differs from the English",
      FigureCheck: "Figure check",
      ConfidenceLanguage: "No likelihood or confidence label",
      SchemaViolation: "Schema violation"
    },
    severities: {
      Info: "Info",
      Warning: "Warning",
      Blocked: "Blocked"
    },
    noPrompts: "No prompt versions captured yet",
    noPromptsHint: "Prompts are captured content-addressed from real agent runs – run any agent to register its current version.",
    versionCount: "{{count}} versions",
    chars: "chars",
    runsWithVersion: "{{count}} runs",
    firstSeen: "first seen",
    cacheHit: "{{rate}}% of input read from the prompt cache",
    evalsHint: "Golden datasets for the deterministic tools, PII redaction, retrieval faithfulness and recorded model cassettes run offline; the live-answer dataset calls the configured model end to end.",
    overallAccuracy: "Overall accuracy {{percent}}%",
    runAll: "Run all evaluations",
    goldenCases: "{{count}} golden cases",
    cassettes: "cassette replay",
    neverRun: "never run",
    driftAlert: "Drift",
    accuracy: "Accuracy",
    faithfulness: "Citation faithfulness",
    schemaValidity: "Schema validity",
    historyHint: "{{count}} runs in history",
    passed: "passed",
    expected: "expected",
    actual: "actual",
    evalDone: "{{count}} dataset(s) evaluated – accuracy {{accuracy}}%",
    driftAlerts: "{{count}} agent(s) drifting",
    driftAlertsHint: "Confidence fell by 10 points or the rejection rate rose by 15 points against the baseline.",
    noDrift: "No drift detected",
    noDriftHint: "Confidence and rejection rates over the last {{days}} days are within tolerance of the baseline.",
    driftTable: "Agent drift",
    driftTableHint: "Current window ({{days}} days) versus everything before it; weekly confidence trend on the right.",
    rejectionRate: "Rejection rate",
    weeklyConfidence: "Weekly confidence",
    stable: "Stable",
    evalDrift: "Evaluation drift alerts",
    piiTokens: "PII tokens",
    piiCases: "across {{count}} cases",
    docsRedacted: "Documents redacted",
    leaksCaught: "Leaks blocked",
    leaksHint: "raw PII caught in model output",
    injections: "Injections flagged",
    injectionsHint: "instructions found inside documents",
    categories: "Categories",
    byCategory: "Tokens by category",
    byCategoryHint: "Pseudonymised before any text leaves the platform.",
    piiCategories: {
      EmiratesId: "Emirates ID",
      Person: "Person",
      Phone: "Phone",
      Email: "E-mail",
      Iban: "IBAN",
      Passport: "Passport",
      Address: "Address"
    },
    piiByAgent: "Redaction by agent",
    piiByAgentHint: "Agents whose inputs required pseudonymisation.",
    risks: {
      tab: "Risk register",
      hint: "Each AI function, the risks it carries and the safeguard that answers each one. The Director or an administrator reviews every entry: high-risk functions every six months, the others every year. The model documentation brings this together with the instructions, tests and use of each function.",
      due: "Due for review: {{count}}",
      allReviewed: "Every entry reviewed",
      allLevels: "All levels",
      download: "Download model documentation",
      levels: {
        Minimal: "Minimal risk",
        Limited: "Limited risk",
        High: "High risk"
      },
      level: "Level of risk",
      residual: "Risk that remains",
      residualIs: "Remaining: {{level}}",
      owner: "Drafts approved by the {{role}}",
      notReviewed: "Not reviewed yet",
      reviewedBy: "Reviewed {{date}} by {{name}}",
      nextReview: "Next review {{date}}",
      overdue: "Review due",
      usage: "Last 30 days: {{runs}} run(s), {{events}} safeguard event(s), {{blocked}} blocked",
      actionsRequired: "Actions required",
      showRisks: "Risks and safeguards ({{count}})",
      record: "Record review",
      recordTitle: "Review: {{name}}",
      recordHint: "Record what you examined and what you concluded. The next review is set from the level you confirm.",
      registerSays: "The register proposes: {{level}}",
      notes: "What was reviewed and concluded",
      notesHint: "At least 20 characters.",
      actions: "Actions that will reduce the risk",
      actionsHint: "Required when a high risk remains.",
      nextHigh: "The next review falls due in six months.",
      nextOther: "The next review falls due in a year.",
      saved: "Review recorded",
      reviewerOnly: "The Director or an administrator records reviews."
    },
    parity: {
      tab: "Arabic and English",
      hint: "Every {{days}} days the platform reads the AI drafts of the last quarter and checks that each function serves Arabic as well as English: the Arabic is not much shorter than the English, it gives the same figures, reviewers do not reject drafts on Arabic files more often than chance explains, and drafts on Arabic files come with Arabic. A measure is judged on {{min}} drafts or more.",
      notRun: "Not checked yet",
      notRunHint: "Run the check to measure the drafts of the last {{days}} days.",
      passed: "No finding in the last check",
      failed: "Findings in the last check: {{count}}",
      summary: "Checked on {{date}} by {{by}} over {{count}} draft(s). The next check falls due on {{next}}.",
      due: "Check due",
      run: "Run the check now",
      ran: "Language-parity check complete",
      runnerOnly: "The Director or an administrator runs the check; it also runs by itself when it falls due.",
      table: "By AI function",
      window: "Drafts from {{from}} to {{to}}.",
      thresholds: "A finding: Arabic under {{coverage}}× the length of the English; figures kept in under {{figures}}% of drafts; a gap in rejections over {{gap}} points that chance does not explain; Arabic missing on over {{missing}}% of drafts on Arabic files.",
      noDrafts: "No AI drafts in the window",
      agent: "AI function",
      drafts: "Drafts",
      bilingual: "{{count}} in both languages",
      coverage: "Arabic length",
      figures: "Figures kept",
      rejected: "Rejected: Arabic / English files",
      missing: "Arabic files without Arabic",
      findingsColumn: "Findings",
      none: "No finding",
      findings: {
        coverage: "Arabic much shorter",
        figures: "Figures missing in Arabic",
        review: "Arabic files rejected more often",
        "missing-arabic": "Arabic missing on Arabic files"
      },
      history: "Previous checks",
      draftsRead: "{{count}} draft(s) read",
      findingCount: "Findings: {{count}}"
    },
    adoption: {
      tab: "How drafts are taken",
      hint: "Whether officers decide or adopt: for each AI function and each officer, how many drafts were approved unchanged, edited or rejected, and whether the reasons given are their own. Drafts of high-risk functions are approved only with the officer's own reasons ({{min}} characters or more, not copied from the draft). Test files are left out.",
      window: "Period",
      days: "Last {{count}} days",
      reviewed: "{{count}} draft(s) reviewed from {{from}} to {{to}}.",
      thresholds: "Looked into: {{rate}}% or more approved unchanged over {{reviews}} reviews or more; the same reasons given {{repeated}} times or more by one officer.",
      functions: "By AI function",
      officers: "By officer",
      function: "AI function",
      officer: "Officer",
      level: "Risk",
      reviewedColumn: "Reviewed",
      unchanged: "Unchanged",
      edited: "Edited",
      rejected: "Rejected",
      medianEdit: "Median edit",
      repeated: "Repeated reasons",
      findingsColumn: "To look into",
      none: "Nothing",
      noReviews: "No reviewed drafts in the period",
      forbidden: "The Director or an administrator reads how officers take the AI's drafts.",
      findings: {
        unchanged: "Nearly all approved unchanged",
        "repeated-reasons": "Same reasons repeated"
      }
    }
  },
  workflowStudio: {
    title: "Workflow Studio",
    subtitle: "Versioned, regulation-aligned workflow definitions. Changes apply to new cases only.",
    definitions: "Definitions",
    templates: "Letter templates",
    holidays: "Holiday calendar",
    fees: "Fee schedule",
    thresholds: "Thresholds",
    advisor: "Change advisor",
    newVersion: "Create new version",
    pendingAdvice: "{{count}} workflow review(s) awaiting the administrator",
    active: "Active",
    draft: "Draft",
    states: "states",
    transitions: "transitions",
    clocks: "statutory clocks",
    agentHooks: "agent hooks",
    editDraft: "Edit draft",
    kind: "Kind",
    actors: "Responsible roles",
    forms: "Forms",
    legalBasis: "Legal basis",
    applicantSees: "Applicant sees",
    hidden: "Hidden",
    onExpiry: "On expiry",
    wd: "working days",
    d: "days",
    version: "Version",
    changeNote: "Change note",
    changeNoteHint: "Why this version exists – it becomes the audit record and the Change Advisor's starting point.",
    selectHint: "Select a step to inspect its roles, forms, statutory clocks and agent hooks.",
    activationAdminOnly: "Activation goes through Releases: add the draft to a release, the advisor reviews it and a second administrator approves. Anyone may draft and request a review.",
    stagedHint: "Draft v{{version}} is staged. Submit the release for approval to activate it for new cases.",
    editingDraft: "Editing draft v{{version}}",
    newVersionFrom: "New version based on v{{version}}",
    unsaved: "Unsaved changes",
    adviceReady: "Advisor review ready",
    validate: "Validate",
    valid: "The graph is valid.",
    invalid: "{{count}} error(s) must be fixed before this version can be activated.",
    saveDraft: "Save as draft version",
    draftSaved: "Draft v{{version}} saved – running cases keep their current version.",
    saveFailed: "The draft could not be saved",
    askAdvisor: "Ask Change Advisor",
    advisorQueued: "The Workflow Change Advisor is reviewing the draft; its assessment will appear in the AI Inbox.",
    deleteDraft: "Delete draft",
    draftDeleted: "Draft deleted",
    activate: "Activate for new cases",
    activated: "Version {{version}} is now active for new cases.",
    addState: "Add state",
    newState: "New state",
    newClock: "New clock",
    validation: "Validation",
    noIssues: "No validation issues.",
    diffTitle: "Changes against active v{{version}}",
    changes: "changes",
    runningCases: "{{count}} running case(s) on the active version",
    noDiff: "No difference from the active version yet.",
    openAdvice: "Open the advisor's review in the AI Inbox",
    selectState: "Select a state to edit",
    editorHint: "Click a state in the diagram to edit its labels, roles, statutory clocks, agent hooks and transitions. Use “Add state” to insert a new step.",
    key: "Key",
    caseStatus: "Case status",
    laneOrder: "Lane · order",
    visibleToApplicant: "Visible to applicant",
    publicLabel: "Public label",
    addClock: "Add clock",
    workingDays: "working days",
    extension: "Maximum extension (days)",
    primary: "primary",
    noExpiryTrigger: "No action on expiry",
    dueFromField: "Due on a recorded date (form field key, e.g. nextHearing)",
    dueFromFieldHint: "The newest value recorded on a completed task, else the intake, sets the due date; the days above are ignored.",
    outgoing: "Outgoing transitions",
    addTransition: "Add transition",
    automatic: "automatic",
    guard: "Guard (e.g. fee.paid)",
    rolesAndClocks: "Roles and clocks",
    completesClocks: "Completes clocks",
    pausesClocks: "Pauses clocks",
    resumesClocks: "Resumes clocks",
    notes: "Notes",
    removeState: "Remove state",
    templatesHint: "Bilingual letter templates used by the Correspondence Agent. Placeholders are filled from the case at send time. An edit, its wording or the response period it states, becomes the template's next version in your draft release and takes effect once a second administrator approves the release.",
    commonTemplates: "Common templates",
    sentCount: "sent {{count}}×",
    placeholders: "Placeholders",
    previewWithCase: "Preview with a real case",
    editTemplate: "Edit template",
    editTemplateHint: "Keep the Arabic and English versions aligned; the official Arabic text prevails.",
    responseDays: "Response period (days)",
    templateSaved: "Template saved as a new version",
    holidaysHint: "Federal holidays skipped by every working-day clock; the calendar cache refreshes immediately.",
    addHoliday: "Add holiday",
    addHolidayHint: "A holiday added later moves every working-day deadline still running across it, and the officer and the party are told.",
    holidayMoved: "Running deadlines moved: {{moved}}, on files: {{cases}}.",
    configStaged: "Staged in your draft release",
    configStagedHint: "«{{title}}» – it takes effect once a second administrator approves the release.",
    openRelease: "Open the release",
    awaitingRelease: "Awaiting release: {{title}}",
    date: "Date",
    holidayAdded: "Holiday added",
    feesHint: "Fees charged by the payment simulator and quoted by the Filing Copilot (Cabinet Decision No. 105 of 2026). A change takes effect only through a release a second administrator approves.",
    refundable: "Refundable",
    cap: "cap AED {{amount}}",
    effectiveFrom: "effective from",
    editFee: "Edit fee",
    editFeeHint: "Percentage fees are expressed as a rate (0.0002 = 0.02%).",
    fixedAmount: "Fixed amount (AED)",
    percentRate: "Rate",
    capAmount: "Cap (AED)",
    feeSaved: "Fee schedule updated",
    thresholdsHint: "Numeric thresholds used by the deterministic calculators (Cabinet Decision No. 3 of 2025 and the Ministry's HHI bands).",
    editThreshold: "Edit threshold",
    editThresholdHint: "Changes take effect for new assessments and are written to the audit trail.",
    value: "Value",
    unit: "Unit",
    thresholdSaved: "Threshold updated",
    advisorHint: "The Workflow Change Advisor reviews every draft version: statutory deadlines, role coverage, agent hooks, applicant-facing changes and roll-out impact. Approving its proposal activates the version.",
    noAdvice: "No workflow reviews yet",
    noAdviceHint: "Draft a new version in the Definitions tab and ask the Change Advisor for a review."
  },
  audit: {
    title: "Audit & Security",
    subtitle: "Tamper-evident audit chain, SIEM stream and role matrix.",
    chainValid: "Hash chain verified",
    chainBroken: "Hash chain broken",
    chainBrokenHint: "{{tampered}} event(s) with a hash mismatch and {{links}} broken link(s); first problem at sequence {{first}}.",
    verify: "Verify chain",
    siem: "SIEM stream",
    siemHint: "events forwarded",
    rbac: "Role matrix",
    securityPosture: "Security posture",
    explorer: "Explorer",
    events: "Audit events",
    last24h: "{{count}} in the last 24 hours",
    last24hPlain: "last 24 hours",
    securityEvents: "Security events",
    warningsCount: "{{count}} warnings",
    logins: "Logins",
    headSequence: "Chain head",
    verifyToSee: "verify to see the head hash",
    activity14d: "Events – last 14 days",
    searchPlaceholder: "Search action, actor, entity…",
    allActions: "All actions",
    allSeverities: "All severities",
    allActors: "All actors",
    pageChainOk: "Links verified on this page",
    pageChainBroken: "Broken link on this page",
    actor: "Actor",
    action: "Action",
    entity: "Entity",
    severity: "Severity",
    chain: "Chain",
    before: "Before",
    after: "After",
    severities: {
      Info: "Info",
      Warning: "Warning",
      Security: "Security"
    },
    actorKinds: {
      Human: "Human",
      System: "System",
      Agent: "AI agent"
    },
    siemStream: "SIEM stream",
    siemStreamHint: "Security and warning events forwarded to the SIEM simulator, newest first.",
    live: "live",
    platformCapabilities: "Platform capabilities",
    platformCapabilitiesHint: "Authorisation policies enforced by the API, by staff role.",
    capability: "Capability",
    policy: "Policy",
    roleShort: {
      EconomicResearcher: "Econ. researcher",
      LegalResearcher: "Legal researcher",
      EconomicExpert: "Econ. expert",
      LegalExpert: "Legal expert",
      HeadOfComplaints: "Head of complaints",
      Director: "Director",
      AssistantUndersecretary: "Asst. undersecretary",
      Undersecretary: "Undersecretary",
      Minister: "Minister",
      JudicialOfficer: "Judicial officer",
      CommitteeMember: "Committee",
      Admin: "Admin"
    },
    statesCount: "{{count}} states",
    state: "State",
    actors: "Actors",
    agents: "Agents",
    system: "system",
    personas: "Demo personas",
    logins7d: "Logins (7 days)",
    loginsFooter: "{{staff}} staff · {{uaePass}} UAE Pass",
    promptInjections: "Prompt injections",
    promptInjectionsHint: "instruction patterns found in documents",
    piiLeaks: "PII leaks blocked",
    piiLeaksHint: "raw identifiers stopped in model output",
    protectedContacts: "Protected identities",
    protectedContactsHint: "whistleblower contacts encrypted at rest",
    controls: "Controls",
    controlsActive: "of {{total}} active",
    controlsTitle: "Security controls",
    controlsHint: "Controls implemented in this demonstrator and how they map to the RFP requirements.",
    recentLogins: "Recent authentication events",
    recentInjections: "Recent prompt-injection findings",
    noInjections: "No prompt-injection attempts detected in case documents."
  },
  analysisPolicy: {
    tab: "Analysis policy",
    hint: "Every number the structural assessment depends on – the calculation method: the statutory thresholds, the bands used to read HHI, the weights of the explainable risk score, the bands confidence is read against, the remedy scenarios offered to officers and the monitoring cadence for decision commitments. Each change is a new version of the method: saving stages it in your draft release, and it reaches the deterministic tools, the agents' facts and the officers' screens only once a second administrator approves the release. Figures, reports and decisions keep the version they were computed by.",
    readOnly: "Read-only — administrators may edit",
    saved: "Staged in release “{{title}}” as calculation method {{version}}",
    reset: "The platform defaults are staged in release “{{title}}” as calculation method {{version}}",
    stage: "Stage in a release",
    stageDefaults: "Stage the defaults",
    method: "Calculation method {{version}}",
    methodApproved: "Approved by {{name}}",
    methodProposed: "Proposed – awaiting the Department's approval",
    pending: "Calculation method {{version}} is waiting in release “{{title}}” ({{status}}). It takes effect once a second administrator approves the release; until then method {{current}} applies.",
    openRelease: "Open the release",
    confidence: "Confidence bands",
    confidenceHint: "The bands every confidence percentage is read against – the platform's reading of a review criterion and an agent's draft – published with the method.",
    confidenceHigh: "High (green) from (%)",
    confidenceMedium: "Medium (amber) from (%)",
    restoreDefaults: "Restore defaults",
    statutory: "Statutory thresholds",
    statutoryHint: "Cabinet Decision No. 3 of 2025 — Article 3 notification thresholds and the Article 2 dominance ratio.",
    turnoverThreshold: "Combined UAE turnover threshold (AED)",
    shareThreshold: "Market-share threshold and dominance ratio (%)",
    crN: "Concentration ratio (CR-n)",
    crNHint: "Number of leading undertakings summed, normally 4.",
    divestiturePresets: "Divestiture scenarios (%)",
    divestitureHint: "Offered as one-click what-if tests on the case analysis tab.",
    bands: "Concentration bands",
    bandsHint: "How HHI and its change are read when classifying a market.",
    hhiModerate: "Moderately concentrated from (HHI)",
    hhiHigh: "Highly concentrated from (HHI)",
    deltaSignificant: "ΔHHI of concern — moderately concentrated",
    deltaMinor: "ΔHHI of concern — highly concentrated",
    deltaPresumption: "ΔHHI flagging likely competition concerns (screen, not a legal presumption)",
    riskWeights: "Structural triage score weights",
    riskWeightsHint: "The explainable 0–100 structural score used to prioritise scrutiny (triage and screening). It never drives the recommendation.",
    shareBands: "Combined-share points",
    deltaBands: "ΔHHI points",
    bandSyntax: "Format: threshold:points, highest first — e.g. 60:30, 40:24, 30:14.",
    pointsHigh: "Points — highly concentrated",
    pointsModerate: "Points — moderately concentrated",
    pointsVertical: "Points — vertical / conglomerate overlap",
    pointsSensitive: "Points — sensitive sector",
    pointsPerConcern: "Points per interested-party concern",
    pointsPerObjection: "Points per admissible objection",
    monitoring: "Post-decision monitoring",
    monitoringHint: "Defaults applied when a commitments register is created from a decision.",
    reviewInterval: "Review interval (days)",
    reminderLead: "Reminder lead time (days)",
    throughRelease: "A change takes effect only through a release approved by a second administrator, as a new version of the calculation method; drafts, reports and decisions keep the version they were made with.",
    exPost: "Ex-post evaluation",
    exPostHint: "When a closed decision is re-read against the Data Bank, and how far the observed structure and prices may drift from the report's predictions before the decision counts as a miss.",
    exPostMonths: "Months after closing",
    exPostHhiTolerance: "HHI tolerance (points)",
    exPostPriceTolerance: "Price tolerance (percentage points)",
    lessons: "Lessons from ex-post evaluations",
    lessonsHint: "What completed evaluations recorded for this policy – read them before moving a margin, a recapture ratio or an entry assumption.",
    noLessons: "No completed evaluations yet",
    noLessonsHint: "Lessons appear here once an ex-post evaluation is recorded on a closed case.",
    evaluations: "Evaluations scheduled",
    evaluationsHint: "Every decision the Department has undertaken to evaluate, due first.",
    noEvaluations: "No evaluations are scheduled."
  },
  integrations: {
    title: "Integrations Monitor",
    subtitle: "Health, latency and audit of every government system integration.",
    endpoint: "Endpoint",
    health: "Health",
    latency: "Latency (p95)",
    calls: "Calls (24h)",
    errors: "Errors",
    breaker: "Circuit breakers",
    simulateFailure: "Simulate outage",
    restore: "Restore",
    systems: "Systems",
    healthyOf: "{{count}} healthy",
    last24h: "last 24 hours",
    errorRate: "{{rate}}% error rate",
    worstP95: "slowest system p95",
    breakersOpen: "open",
    systemsDown: "{{count}} system(s) down",
    allHealthy: "All systems healthy",
    traffic: "Traffic and latency",
    trafficHint: "Calls per system, failures and p95 latency over time.",
    healths: {
      Unknown: "Unknown",
      Healthy: "Healthy",
      Degraded: "Degraded",
      Down: "Down"
    },
    circuitOpen: "Circuit open",
    noCalls: "no calls yet",
    consecutiveFailures: "{{count}} consecutive failure(s) – trips at 5",
    probe: "Probe",
    probeResult: "{{key}}: {{outcome}} in {{ms}} ms",
    openBreaker: "Open breaker",
    closeBreaker: "Close breaker",
    breakerOpened: "{{key}}: circuit opened – calls are refused",
    breakerClosed: "{{key}}: circuit closed",
    manualReason: "Opened manually from the Integrations Monitor",
    outage: "Outage",
    outageOn: "{{key}}: simulator now returns 503",
    outageOff: "{{key}}: simulator restored",
    latencyMs: "Injected latency (ms)",
    successRate: "Success rate",
    failedOnly: "Failed only",
    operation: "Operation",
    trace: "Trace"
  },
  redraft: {
    button: "Redraft with AI",
    title: "Redraft with AI",
    hint: "Say what needs changing. The assistant rewrites the text and shows it beside the current one — facts, figures and citations are kept, nothing is saved until you apply it.",
    instruction: "What needs changing?",
    instructionPlaceholder: "e.g. Shorter and more formal; say that the bank receipt for the filing fee is still missing; put the deficiencies first…",
    suggestions: {
      formal: "More formal",
      shorter: "Shorter",
      clearer: "Clearer and better structured",
      grammar: "Fix grammar and typos",
      translateAr: "Translate to Arabic",
      translateEn: "Translate to English",
      fromContext: "Write it for me from the criterion, my verdict and what the platform found"
    },
    run: "Redraft",
    rerun: "Try again",
    apply: "Apply redraft",
    applied: "Redraft applied — review it, then record or approve as usual",
    current: "Current text",
    proposed: "Proposed redraft",
    waiting: "The redraft appears here.",
    empty: "The text is empty — the assistant will write it from your instruction and the context.",
    showChanges: "Show changes",
    hideChanges: "Hide changes",
    unchanged: "The assistant returned the text unchanged. Try a more specific instruction.",
    offline: "AI redrafting is unavailable while the platform runs without a language model; edit the text by hand.",
    flags: "Instructions hidden inside the text were detected and ignored; read the result with care.",
    failed: "The redraft failed",
    meter: "{{model}} · {{ms}} ms"
  },
  inbox: {
    proposedAction: "Proposed action",
    reviseHint: "Tell the agent what to change. The current draft is rejected with your note and the agent redrafts immediately; the note also feeds future runs.",
    aiDraft: "The agent's draft",
    yourVersion: "Your version — what will be approved",
    editHintChanged: "Your edits will be recorded as feedback for the agent's evaluation.",
    editHintNoChanges: "No changes yet.",
    resetToDraft: "Discard your edits and start again from the agent's draft",
    reviewNote: "Review note (optional)",
    approveAndExecute: "Approve & execute",
    reviewNoteRequired: "Review note",
    reviseNoteHint: "Be concrete: which section, which article, which figure. The agent quotes your note back in its activity feed.",
    rejectNoteHint: "Why is this proposal not acceptable? Rejection notes are fed back to the agent on its next runs."
  },
  workspace: {
    exPost: {
      tab: "Ex-post",
      title: "Ex-post evaluation of the decision",
      hint: "What the report predicted for each market – structure, prices, entry – and what the commitments were to achieve, read against the Data Bank after the decision. Complete it with a verdict, findings and the lessons the analysis policy should take.",
      none: "No ex-post evaluation is scheduled",
      noneHint: "The evaluation is scheduled when the decided case closes.",
      status: {
        Scheduled: "Scheduled",
        Read: "Read",
        Completed: "Completed"
      },
      verdicts: {
        AsPredicted: "As predicted",
        BetterThanPredicted: "Better than predicted",
        WorseThanPredicted: "Worse than predicted",
        Inconclusive: "Inconclusive",
        NoData: "No data"
      },
      entryReadings: {
        Likely: "Likely",
        Unlikely: "Unlikely",
        NotAssessed: "Not assessed"
      },
      read: "Read the Data Bank",
      reread: "Re-read the Data Bank",
      readDone: "The Data Bank was read against the report's predictions.",
      complete: "Record the evaluation",
      completed: "The evaluation is recorded and its lessons are on the analysis policy page.",
      record: "Record",
      decision: "Decision",
      dueAt: "Due",
      inDays: "in {{count}} day(s)",
      overdueDays: "{{count}} day(s) overdue",
      lastRead: "Last read",
      efficiencies: "Efficiencies claimed",
      cognisable: "cognisable",
      suggested: "Suggested verdict",
      notRead: "The Data Bank has not been read for this decision yet. Read it to compare what the market did with what the report predicted.",
      hhiPredicted: "HHI predicted",
      hhiObserved: "HHI observed",
      sharePredicted: "Merged share predicted",
      shareObserved: "Merged share observed",
      pricePredicted: "Price effect simulated",
      priceObserved: "Price change observed",
      entry: "Entry",
      verdict: "Verdict",
      commitments: "Commitments",
      suggestedLessons: "Suggested lessons for the analysis policy",
      findings: "Findings",
      findingsHint: "What the market did against what the report predicted, market by market, and whether the commitments held.",
      policyLessons: "Lessons for the analysis policy",
      policyLessonsHint: "What the margin, recapture, entry or remedy assumptions should take from this case – or that nothing needs changing, and why.",
      completedBy: "Completed by {{name}} · {{date}}"
    }
  }
} as const;
